import { useEffect, useState, useRef } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import useCreateProjectStore from '@/store/CreateProjectStore';
import ScreeningQuestionBox from './components/ScreeningQuestionBox';
import { ScreeningQuestionType, ScreeningRulesTypes } from '@/enums';
import { ScreeningProps, ScreeningQuestion, ScreeningResponse } from '@/types';
import ToolTip from '@/components/tooltip/ToolTip';
import useScreeningQuestionsStore from '@/store/ScreeningQuestionsStore';

const singleSelectDefaultAnswers = [
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
];

const multiSelectDefaultAnswers = [
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
];

const numericDefaultAnswers = {};

const MAX_QUESTIONS = 25;

function ScreeningQuestions({
  isProjectPublished,
  isProjectCompleted,
  setValue,
  onSubmit,
  screeningQuestions,
  register,
  errors,
  clearErrors,
}: ScreeningProps) {
  const { lang } = useTranslation();
  const [questions, setQuestions] = useState<ScreeningQuestion[]>(screeningQuestions || []);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);
  const draftQuestionsRef = useRef<ScreeningQuestion[]>([]);
  const { isUserViewer } = useWorkspaceRoles();
  const { setScreeningQuestionsLength } = useScreeningQuestionsStore();
  const {
    setReorderChanged,
    setIsFormChanged,
    setDuplicatedScreeningQuestion,
    setDuplicatedScreeningQuestionAnswer,
    isFormChanged,
  } = useCreateProjectStore();

  const updateQuestions = (updatedQuestions: ScreeningQuestion[]) => {
    setQuestions(updatedQuestions);
    draftQuestionsRef.current = draftQuestionsRef.current.map(
      (q) => updatedQuestions.find((updatedQ) => updatedQ.id === q.id) || q,
    );
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });
  };

  const updateQuestionById = (
    questionId: string,
    updater: (question: ScreeningQuestion) => ScreeningQuestion,
  ) => {
    const updatedQuestions = questions.map((question) =>
      question.id === questionId ? updater(question) : question,
    );
    updateQuestions(updatedQuestions);
  };

  const getResponsesForType = (newType: ScreeningQuestionType) => {
    const responseActions = {
      [ScreeningQuestionType.SINGLE_SELECT]: singleSelectDefaultAnswers,
      [ScreeningQuestionType.MULTI_SELECT]: multiSelectDefaultAnswers,
    };

    return responseActions[newType as keyof typeof responseActions] || [];
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setQuestions((prevQuestions) => {
      const oldIndex = prevQuestions.findIndex((q) => q.id === active.id);
      const newIndex = prevQuestions.findIndex((q) => q.id === over.id);

      const reorderedQuestions = arrayMove(prevQuestions, oldIndex, newIndex);
      setValue('screening_questions', reorderedQuestions, { shouldDirty: true });
      setReorderChanged(true);
      return reorderedQuestions;
    });

    setIsActiveDrag(false);
  };

  const handleAddNewQuestion = () => {
    const newQuestion: ScreeningQuestion = {
      id: uuidv4(),
      question: '',
      type: ScreeningQuestionType.MULTI_SELECT,
      responses: [...multiSelectDefaultAnswers],
    };

    draftQuestionsRef.current = [...draftQuestionsRef.current, newQuestion];
    updateQuestions([...(questions || []), newQuestion]);
    setOpenQuestionId(newQuestion.id);
  };

  const handleDuplicatedQuestions = (newValue: string, questionId: string) => {
    if (!newValue) return;

    const isDuplicate = questions.some(
      ({ question }) => question.toLowerCase() === newValue.toLowerCase(),
    );

    setDuplicatedScreeningQuestion(isDuplicate ? { id: questionId } : { id: '' });
  };

  const handleDuplicatedAnswers = (
    newValue: string,
    answerId: string,
    responses: ScreeningResponse[],
  ) => {
    if (!newValue) return;
    const isDuplicate = responses.some(({ value }) => {
      return value.toLowerCase() === newValue.toLowerCase();
    });
    setDuplicatedScreeningQuestionAnswer(isDuplicate ? { id: answerId } : { id: '' });
  };

  const handleQuestionTextChange = (questionId: string, newValue: string) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    if (questionIndex >= 0) {
      clearErrors(`screening_questions.${questionIndex}.question`);
    }
    if (isFormChanged) handleDuplicatedQuestions(newValue, questionId);
    updateQuestionById(questionId, (question) => ({ ...question, question: newValue }));
  };

  const handleAnswersOrderChange = (questionId: string, answers: ScreeningResponse[]) => {
    updateQuestionById(questionId, (question) => ({
      ...question,
      responses: answers,
    }));
  };

  const onRandomizeAnswersChange = (questionId: string, randomize: boolean) => {
    updateQuestionById(questionId, (question) => ({
      ...question,
      randomize,
    }));
    setValue(
      `screening_questions.${questions.findIndex((q) => q.id === questionId)}.randomize`,
      randomize,
      {
        shouldDirty: true,
      },
    );
  };

  const onQuotasEnabledChange = (questionId: string, enabled: boolean) => {
    setIsFormChanged(true);
    updateQuestionById(questionId, (question) => {
      const updatedResponses = enabled
        ? question.responses
        : question.responses?.map((r) => ({ ...r, quota_percentage: undefined }));
      return {
        ...question,
        quotas_enabled: enabled,
        responses: updatedResponses,
      };
    });
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    setValue(`screening_questions.${questionIndex}.quotas_enabled`, enabled, {
      shouldDirty: true,
    });
  };

  const onQuotaPercentageChange = (
    questionId: string,
    answerId: string,
    percentage: number | undefined,
  ) => {
    setIsFormChanged(true);
    updateQuestionById(questionId, (question) => ({
      ...question,
      responses: question.responses?.map((r) =>
        r.id === answerId ? { ...r, quota_percentage: percentage } : r,
      ),
    }));
  };

  const handleQuestionTypeChange = (questionId: string, newType: ScreeningQuestionType) => {
    updateQuestionById(questionId, ({ id, question }) => ({
      id,
      question,
      type: newType,
      quotas_enabled: false,
      ...(newType === ScreeningQuestionType.NUMERIC
        ? { numericResponse: numericDefaultAnswers }
        : { responses: getResponsesForType(newType) }),
    }));
  };

  const clearAnswerError = (
    questionId: string,
    answerId: string,
    field: 'value' | 'rule' | 'numericResponse',
  ) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const responseIndex = questions[questionIndex]?.responses?.findIndex((r) => r.id === answerId);

    if (questionIndex >= 0) {
      if (field === 'numericResponse') {
        clearErrors(`screening_questions.${questionIndex}.numericResponse`);
      } else if (typeof responseIndex === 'number' && responseIndex >= 0) {
        clearErrors(`screening_questions.${questionIndex}.responses.${responseIndex}.${field}`);
      }
    }
  };

  const handleQuestionAnswerChange = (
    questionId: string,
    answerId: string,
    newText: string | { minimum?: number; maximum?: number },
    newRule?: ScreeningRulesTypes | null,
  ) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const responseIndex = questions[questionIndex]?.responses?.findIndex((r) => r.id === answerId);

    updateQuestionById(questionId, (question) => {
      if (question.type === ScreeningQuestionType.NUMERIC) {
        return {
          ...question,
          numericResponse: {
            ...question.numericResponse,
            ...(typeof newText !== 'string' &&
              newText.minimum !== undefined && { minimum: +newText.minimum }),
            ...(typeof newText !== 'string' &&
              newText.maximum !== undefined && { maximum: +newText.maximum }),
          },
        };
      }
      // For non-numeric types
      const responses = question.responses || [];
      const responseExists = responses.some((response) => response.id === answerId);

      const answer =
        typeof responseIndex === 'number'
          ? questions[questionIndex]?.responses?.[responseIndex]
          : undefined;
      const hasAnswerChanged = answer?.value !== newText;
      if (isFormChanged && hasAnswerChanged) {
        handleDuplicatedAnswers(newText as string, answerId, responses);
      }
      const updatedQuestion = {
        ...question,
        responses: responseExists
          ? responses.map((response) =>
              response.id === answerId
                ? {
                    ...response,
                    value: newText as string,
                    rule: newRule,
                  }
                : response,
            )
          : [
              ...responses,
              {
                id: answerId,
                rule: newRule,
                value: newText as string,
              },
            ],
      };

      setValue(`screening_questions.${questionIndex}.responses.${responseIndex}.rule`, newRule, {
        shouldDirty: true,
      });

      return updatedQuestion;
    });
  };

  const handleDeleteAnswer = (questionId: string, answerId: string) => {
    setIsFormChanged(true);
    const questionIndex = questions.findIndex((q) => q.id === questionId);

    let screeningResponse: ScreeningResponse[] = [];
    const updatedQuestions = questions.map((question) => {
      if (question.id === questionId) {
        screeningResponse = (question.responses || []).filter(
          (response) => response.id !== answerId,
        );
        return {
          ...question,
          responses: screeningResponse,
        };
      }
      return question;
    });

    clearErrors(`screening_questions.${questionIndex}.responses`);

    setQuestions(updatedQuestions);
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });

    if (
      questions.some((q) => {
        if (q.id === questionId && q.type !== ScreeningQuestionType.NUMERIC) {
          const responses = q.responses || [];
          return responses.length < 2;
        }
        return false;
      })
    ) {
      setIsFormChanged(false);
    }
  };

  const handleDeleteQuestion = (idToDelete: string) => {
    const isQuestionFromDraft = !screeningQuestions?.some(
      (q: { id: string }) => q.id === idToDelete,
    );
    draftQuestionsRef.current = draftQuestionsRef.current.filter((q) => q.id !== idToDelete);
    const questionIndex = questions.findIndex((q) => q.id === idToDelete);
    clearErrors(`screening_questions.${questionIndex}`);
    const updatedQuestions = questions.filter((question) => question.id !== idToDelete);
    setQuestions(updatedQuestions);
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });
    if (!isQuestionFromDraft) {
      const updatedInterviewQuestionsForDb = screeningQuestions?.filter(
        (q: { id: string }) => q.id !== idToDelete,
      );
      onSubmit({ screening_questions: updatedInterviewQuestionsForDb }, true);
    }
  };

  useEffect(() => {
    setValue('screening_questions', questions, { shouldDirty: true });
  }, [questions, setValue]);

  useEffect(() => {
    if (questions && screeningQuestions) {
      const localQuestions = questions.filter(
        (q) => !screeningQuestions.some((dbQ: { id: string }) => dbQ.id === q.id),
      );
      draftQuestionsRef.current = localQuestions;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screeningQuestions) {
      const combinedQuestions = [...screeningQuestions, ...draftQuestionsRef.current];
      const uniqueQuestions = combinedQuestions.filter(
        (question, index, self) => index === self.findIndex((q) => q.id === question.id),
      );
      setQuestions(uniqueQuestions);
    }
  }, [screeningQuestions]);

  useEffect(() => {
    setScreeningQuestionsLength(questions?.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions?.length]);

  const isAddScreeningQuestionDisabled = isProjectPublished || isProjectCompleted;
  const isButtonDisabled = isAddScreeningQuestionDisabled || questions?.length >= MAX_QUESTIONS;
  const isSortableDisabled = isUserViewer || isAddScreeningQuestionDisabled;
  const hasQuestions = questions?.length > 0;

  return (
    <div className="flex flex-col flex-grow w-full max-h-full">
      <div className="flex flex w-full justify-between items-center">
        <h4 className="flex items-center gap-1 text-[15px] font-medium">
          {lang.get('msg.screeningQuestions')}
        </h4>
        <ToolTip
          text={lang.get('msg.screeningQuestionsTooltipText')}
          className="max-w-lg border-[3px] border-primary text-sm z-100"
        />
      </div>

      {/* questions area */}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={() => setIsActiveDrag(true)}
      >
        <div className="flex flex-col w-full h-full gap-5 p-6 my-5 rounded-md bg-slate-50">
          {!hasQuestions && (
            <p className="text-sm font-medium">{lang.get('msg.noScreeningQuestions')}</p>
          )}

          <SortableContext
            disabled={isSortableDisabled}
            items={questions}
            strategy={verticalListSortingStrategy}
          >
            {questions?.map((question, index) => {
              const responses =
                question.type === ScreeningQuestionType.NUMERIC
                  ? question.numericResponse
                  : question.responses;
              return (
                <ScreeningQuestionBox
                  key={question.id}
                  error={errors?.screening_questions?.[index]}
                  index={index}
                  isActiveDrag={isActiveDrag}
                  id={question.id}
                  type={question.type}
                  randomize={question?.randomize}
                  onRandomizeAnswersChange={onRandomizeAnswersChange}
                  question_text={question.question}
                  responses={responses}
                  openQuestionId={openQuestionId}
                  isProjectCompleted={isProjectCompleted}
                  isProjectPublished={isProjectPublished}
                  isUserViewer={isUserViewer}
                  register={register}
                  setOpenQuestionId={setOpenQuestionId}
                  handleDeleteQuestion={handleDeleteQuestion}
                  handleQuestionTextChange={handleQuestionTextChange}
                  handleQuestionTypeChange={handleQuestionTypeChange}
                  handleQuestionAnswerChange={handleQuestionAnswerChange}
                  clearAnswerError={clearAnswerError}
                  handleDeleteAnswer={handleDeleteAnswer}
                  handleAnswersOrderChange={handleAnswersOrderChange}
                  quotasEnabled={question.quotas_enabled}
                  onQuotasEnabledChange={onQuotasEnabledChange}
                  onQuotaPercentageChange={onQuotaPercentageChange}
                />
              );
            })}
          </SortableContext>
        </div>
      </DndContext>
      <Button
        onClick={handleAddNewQuestion}
        disabled={isButtonDisabled}
        variant="default"
        type="button"
        className="mb-2"
      >
        <IconPlus size={16} className="mr-1" />
        {lang.get('msg.addScreeningQuestion')}
      </Button>
    </div>
  );
}

export default ScreeningQuestions;
